import { SearchOutlined } from '@ant-design/icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import Spin from 'antd/lib/spin';
import Typography from 'antd/lib/typography';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { PRIMARY_COLOR } from '../../../theme';
import { BaseFilterAccordion } from './BaseFilterAccordion';
import { useResizable } from './useResizable';

const RESIZE_HANDLE_HEIGHT = 6;
const MAX_WIDTH = 160; // Maximum width for the select box

interface PrimalSelectOption<T = number> {
    label: string;
    value: T;
}

interface BaseFilterSelectCheckboxProps<T = number> {
    options: PrimalSelectOption<T>[];
    value: T[];
    onChange: (value: T[]) => void;
    label?: string;
    disabled?: boolean;
    loading?: boolean;
    searchable?: boolean;
    showClear?: boolean;
    selectedCountInLabel?: boolean;
    height?: number;
    showSelectAllOption?: boolean;
    multiple?: boolean;
    resizable?: boolean;
    minHeight?: number;
    maxHeight?: number;
    onHeightChange?: (height: number) => void;
}

export function BaseFilterCheckboxSelect<T = number>({
    options,
    value,
    onChange,
    label,

    disabled = false,
    loading = false,
    searchable = true,
    multiple = true,
    showSelectAllOption = false,
    resizable = false,
    showClear = true,
    selectedCountInLabel = false,

    height = 300,
    minHeight = 200,
    maxHeight = 600,

    onHeightChange,
}: BaseFilterSelectCheckboxProps<T>) {
    const [searchTerm, setSearchTerm] = useState('');
    const { filteredOptions, handleCheckboxChange, handleClear, handleSelectAll } = useFilterSelect(
        options,
        value,
        onChange,
        searchable,
        searchTerm,
        multiple
    );

    const { height: currentHeight, isResizing, handleMouseDown } = useResizable(height, minHeight, maxHeight, onHeightChange);

    // Calculate the list height (subtracting header height)
    const listHeight = useMemo(() => {
        const searchHeight = searchable ? 34 : 0;
        const selectAllHeight = showSelectAllOption && multiple ? 34 : 0;

        return currentHeight - searchHeight - selectAllHeight - (resizable ? RESIZE_HANDLE_HEIGHT : 0);
    }, [searchable, showSelectAllOption, multiple, currentHeight, resizable]);

    const labelWithCount = useMemo(() => {
        if (!selectedCountInLabel || !value?.length) return label;

        const count = value.length;

        return `${label} (${count})`;
    }, [label, value?.length, selectedCountInLabel]);

    return (
        <BaseFilterAccordion label={labelWithCount} value={value} onClear={handleClear}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                    height: resizable ? currentHeight : height,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',

                        padding: '0 8px',
                        gap: '4px',
                    }}
                >
                    {searchable && (
                        <Input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search"
                            addonAfter={<SearchOutlined />}
                            style={{ backgroundColor: 'white', borderRadius: '5px' }}
                        />
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        {showSelectAllOption && multiple && (
                            <Button size="small" type="link" variant="text" onClick={handleSelectAll} disabled={disabled} style={{ padding: 0 }}>
                                Select All
                            </Button>
                        )}
                    </div>
                </div>

                <div style={{ overflow: 'hidden' }}>
                    {/* Loading */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: listHeight }}>
                            <Spin spinning />
                        </div>
                    ) : null}

                    {/* No options */}
                    {!loading && filteredOptions.length === 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: listHeight }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    ) : null}

                    {/* Options List */}
                    {!loading && filteredOptions.length !== 0 && (
                        <VirtualizedOptionsList
                            height={listHeight}
                            options={filteredOptions}
                            value={value}
                            disabled={disabled}
                            onCheckboxChange={handleCheckboxChange}
                        />
                    )}
                </div>

                {/* Resize Handle */}
                {resizable && (
                    <div
                        onMouseDown={handleMouseDown}
                        style={{
                            height: RESIZE_HANDLE_HEIGHT,
                            cursor: 'ns-resize',
                            backgroundColor: isResizing ? PRIMARY_COLOR : 'transparent',
                            borderTop: '1px solid #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s',
                            position: 'relative',
                        }}
                    >
                        <div
                            style={{
                                width: '20px',
                                height: '2px',
                                backgroundColor: isResizing ? '#ffffff' : '#bfbfbf',
                                borderRadius: '1px',
                                transition: 'background-color 0.2s',
                            }}
                        />
                    </div>
                )}
            </div>
        </BaseFilterAccordion>
    );
}

export function useFilterSelect<T>(
    options: { label: string; value: T }[],
    value: T[],
    onChange: (value: T[]) => void,
    searchable: boolean,
    searchTerm: string,
    multiple: boolean
) {
    const filteredOptions = useMemo(() => {
        if (!searchable || !searchTerm) return options;

        const lowerSearchTerm = searchTerm.toLowerCase();

        return options.filter(opt => opt.label.toLowerCase().includes(lowerSearchTerm));
    }, [options, searchTerm, searchable]);

    const filteredOptionValues = useMemo(() => filteredOptions.map(opt => opt.value), [filteredOptions]);

    const handleCheckboxChange = useCallback(
        (checked: boolean, changedValue: T) => {
            if (!multiple) {
                onChange(checked ? [changedValue] : []);
                return;
            }

            const newValue: T[] = checked ? [...value, changedValue] : value.filter(v => v !== changedValue);

            onChange(newValue);
        },
        [onChange, value, multiple]
    );

    const handleClear = useCallback(() => {
        onChange([]);
    }, [onChange]);

    const handleSelectAll = useCallback(() => {
        const allVisible = filteredOptionValues.filter(id => !value.includes(id));

        onChange([...value, ...allVisible]);
    }, [filteredOptionValues, onChange, value]);

    return {
        filteredOptions,
        filteredOptionValues,
        handleCheckboxChange,
        handleClear,
        handleSelectAll,
    };
}

// Virtualized Options List Component
interface VirtualizedOptionsListProps<T = number> {
    height: number;
    options: PrimalSelectOption<T>[];
    value: T[];
    disabled: boolean;
    onCheckboxChange: (checked: boolean, value: T) => void;
}

function VirtualizedOptionsList<T = number>({ height, options, value, disabled, onCheckboxChange }: VirtualizedOptionsListProps<T>) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 34, // Adjust based on your option height
        overscan: 5, // Number of items to render outside the visible area
        useAnimationFrameWithResizeObserver: true, // Use animation frame for smoother scrolling
        paddingEnd: 8, // Add some padding to the end for better scrolling experience
    });

    return (
        <div
            ref={parentRef}
            style={{
                height,
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map((virtualItem: any) => {
                    const option = options[virtualItem.index];

                    return (
                        <div
                            key={virtualItem.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start}px)`,
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                            }}
                        >
                            <Checkbox
                                disabled={disabled}
                                checked={value.includes(option.value)}
                                onChange={e => onCheckboxChange(e.target.checked, option.value)}
                                value={option.value}
                            >
                                <Typography.Text
                                    ellipsis={{ tooltip: { title: option.label, placement: 'right' } }}
                                    style={{ fontSize: '12px', lineHeight: '20px', maxWidth: MAX_WIDTH }}
                                >
                                    {option.label}
                                </Typography.Text>
                            </Checkbox>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
